import { offerBillingLabel } from '../../lib/offerBilling';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useScope } from '@workspace/scope-context';
import {
  computeMapGeometryBounds,
  MapWidgetView,
  type MapWorkbenchViewState,
} from '@workspace/ui';
import {
  Callout,
  DescriptionText,
  HorizontalStack,
  PaneFooter,
  PaneHeader,
  PaneSection,
  RegistryStatus,
  Surface,
  VerticalStack,
  WizardWorkspace,
} from '@workspace/silk-ui';
import { CheckCircle2, MapPin, Settings2, ShoppingCart, User } from 'lucide-react';
import { useNavigation } from '@/context/NavigationContext';
import { usePreferences } from '@/context/PreferencesContext';
import {
  type AdminEligibilityPreviewPreliminaryEstimate,
  getCommerceErrorMessage,
  listResellerEntitlements,
  type AdminEligibilityPreviewProduct,
  type AdminEligibilityPreviewSellableVariant,
  type CheckoutOffer,
  type ResellerEntitlement,
} from '@/lib/commerce';
import {
  clearSalesRequestWizardDraft,
  createSalesRequestCart,
  createSalesRequestQuote,
  evaluateSalesRequest,
  parseSalesRequestWizardDraft,
  type SalesRequestEvaluation,
  type SalesRequestServiceOption,
} from '@/lib/sales-routing';
import { composeStyleWithSystemBasemap } from '@/lib/systemBasemap';

type SelectionState = Record<string, number>;
type OfferSelectionState = Record<string, string>;

interface SalesRequestVariantOption {
  offer_id?: string;
  kind: string;
  product_id: string;
  variant_id: string;
  display_name: string;
  product_name: string;
  description?: string;
  class?: string;
  limit_per_cart?: number;
  offers: CheckoutOffer[];
}

const debugSalesRequestRoutePreview = (message: string, details?: Record<string, unknown>) => {
  if (
    !import.meta.env.DEV ||
    import.meta.env.MODE === 'test' ||
    typeof console === 'undefined' ||
    typeof console.debug !== 'function'
  ) {
    return;
  }
  if (details) {
    console.debug(`[SalesRequest][RoutePreview] ${message}`, details);
    return;
  }
  console.debug(`[SalesRequest][RoutePreview] ${message}`);
};

const inputCls =
  'h-11 w-full rounded-lg border border-border/60 bg-background px-4 text-sm font-medium text-foreground outline-none transition-all focus:border-primary/40 focus:ring-4 focus:ring-primary/5 disabled:opacity-50';

const selectCls =
  'h-11 w-full rounded-lg border border-border/60 bg-background px-4 text-sm font-medium text-foreground outline-none transition-all focus:border-primary/40 focus:ring-4 focus:ring-primary/5 disabled:opacity-50';

const ROUTE_PREVIEW_MAP_HEIGHT_PX = 360;
const ROUTE_PREVIEW_FALLBACK_WIDTH_PX = 640;
const ROUTE_PREVIEW_TILE_SIZE = 512;
const ROUTE_PREVIEW_MIN_ZOOM = 3;
const ROUTE_PREVIEW_MAX_ZOOM = 14;
const ROUTE_PREVIEW_ZOOM_SAFETY_MARGIN = 0.35;
const ROUTE_PREVIEW_PADDING = {
  top: 36,
  right: 36,
  bottom: 36,
  left: 36,
} as const;

type RoutePreviewCoordinate = [number, number];

function FormField({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-black uppercase tracking-widest text-muted-foreground/80">{label}</label>
      {children}
      {hint ? <p className="text-[10px] font-medium text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

function variantSelectionKey(option: Pick<SalesRequestVariantOption, 'kind' | 'product_id' | 'variant_id'>): string {
  return [option.kind, option.product_id, option.variant_id].join('::');
}

function formatCurrency(currency: string, amount: number): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
  } catch {
    return `${currency} ${amount}`;
  }
}

function formatEstimateCurrency(amount: number | null | undefined, currency?: string | null): string {
  if (amount == null) {
    return 'Not available';
  }
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency || 'USD'} ${amount}`;
  }
}

function formatRoutingProfile(profile: string): string {
  if (profile === 'fiber_construction') {
    return 'Fiber Construction';
  }
  if (profile === 'standard') {
    return 'Standard';
  }
  return profile;
}

function buildOfferFallback(option: SalesRequestServiceOption): CheckoutOffer {
  return {
    offer_id: option.offer_id || '',
    variant_id: option.variant_id,
    offer_key: option.offer_id || '',
    name: option.offer_display_name || option.display_name,
    display_name: option.offer_display_name || option.display_name,
    offer_type: 'standard',
    is_default: true,
    sellable: true,
    price: option.price ?? 0,
    discounted_price: option.discounted_price ?? option.price ?? 0,
    discount_length: 0,
    startup_fee: 0,
    min_price: option.price ?? 0,
    max_price: option.price ?? 0,
    currency: option.currency || '',
    charge_type: option.charge_type,
    billing_period_months: option.billing_period_months,
    timing: undefined,
    metadata: {},
    charges: [],
    discounts: [],
  };
}

function buildVariantServiceOptions(evaluation: SalesRequestEvaluation | null): SalesRequestVariantOption[] {
  if (!evaluation) return [];

  const flatOptions = evaluation.sellable_service_options || [];
  const sellableProducts = evaluation.evaluation.sellable_products || evaluation.sellable_products || [];
  const flatByVariant = new Map<string, SalesRequestServiceOption[]>();
  for (const option of flatOptions) {
    const key = `${option.product_id}::${option.variant_id}`;
    const existing = flatByVariant.get(key);
    if (existing) {
      existing.push(option);
    } else {
      flatByVariant.set(key, [option]);
    }
  }

  const variantOptions: SalesRequestVariantOption[] = [];
  const pushVariant = (product: AdminEligibilityPreviewProduct, variant: AdminEligibilityPreviewSellableVariant) => {
    const variantKey = `${product.product_id}::${variant.variant_id}`;
    const matchingOptions = flatByVariant.get(variantKey) || [];
    const baseOption = matchingOptions.find(item => item.offer_id === variant.offer_id) || matchingOptions[0];
    const explicitOffers = (variant.offers || []).filter(offer => offer.sellable);
    const fallbackOffers = matchingOptions
      .filter(item => item.offer_id)
      .map(buildOfferFallback)
      .filter((offer, index, offers) => offers.findIndex(candidate => candidate.offer_id === offer.offer_id) === index);
    const offers = explicitOffers.length > 0 ? explicitOffers : fallbackOffers;

    if (!baseOption) return;

    variantOptions.push({
      kind: baseOption.kind,
      product_id: product.product_id,
      variant_id: variant.variant_id,
      offer_id: variant.offer_id || baseOption.offer_id || undefined,
      display_name: variant.display_name || baseOption.display_name,
      product_name: product.display_name || product.name || baseOption.product_name,
      description: variant.description || baseOption.description,
      class: variant.class || baseOption.class,
      limit_per_cart: variant.limit_per_cart || baseOption.limit_per_cart,
      offers,
    });
  };

  for (const product of sellableProducts) {
    for (const variant of product.variants || []) {
      pushVariant(product, variant);
    }
  }

  if (variantOptions.length > 0) {
    return variantOptions;
  }

  return flatOptions.map(option => ({
    kind: option.kind,
    product_id: option.product_id,
    variant_id: option.variant_id,
    offer_id: option.offer_id || undefined,
    display_name: option.display_name,
    product_name: option.product_name,
    description: option.description,
    class: option.class,
    limit_per_cart: option.limit_per_cart,
    offers: [buildOfferFallback(option)],
  }));
}

function resolveOfferForVariant(option: SalesRequestVariantOption, selectedOfferID?: string): CheckoutOffer | null {
  if (option.offers.length === 0) return null;
  if (selectedOfferID) {
    const matched = option.offers.find(offer => offer.offer_id === selectedOfferID);
    if (matched) return matched;
  }
  return option.offers.find(offer => offer.isDefault) || option.offers[0];
}

function isRoutePreviewCoordinate(value: unknown): value is RoutePreviewCoordinate {
  return (
    Array.isArray(value) &&
    value.length >= 2 &&
    typeof value[0] === 'number' &&
    Number.isFinite(value[0]) &&
    typeof value[1] === 'number' &&
    Number.isFinite(value[1])
  );
}

function extractRoutePreviewEndpoints(
  routeGeometry: Record<string, unknown> | null | undefined,
): { start: RoutePreviewCoordinate; end: RoutePreviewCoordinate } | null {
  if (!routeGeometry) {
    return null;
  }
  const type = typeof routeGeometry.type === 'string' ? routeGeometry.type : '';
  const coordinates = (routeGeometry as { coordinates?: unknown }).coordinates;
  if (type === 'LineString' && Array.isArray(coordinates)) {
    const start = coordinates.find(isRoutePreviewCoordinate);
    const end = [...coordinates].reverse().find(isRoutePreviewCoordinate);
    return start && end ? { start, end } : null;
  }
  if (type === 'MultiLineString' && Array.isArray(coordinates)) {
    const segments = coordinates.filter(Array.isArray);
    const firstSegment = segments.find(segment => Array.isArray(segment) && segment.some(isRoutePreviewCoordinate));
    const lastSegment = [...segments]
      .reverse()
      .find(segment => Array.isArray(segment) && segment.some(isRoutePreviewCoordinate));
    const start = firstSegment && Array.isArray(firstSegment) ? firstSegment.find(isRoutePreviewCoordinate) : null;
    const end = lastSegment && Array.isArray(lastSegment) ? [...lastSegment].reverse().find(isRoutePreviewCoordinate) : null;
    return start && end ? { start, end } : null;
  }
  return null;
}

function buildRoutePreviewOverlayGeoJSON(routeGeometry: Record<string, unknown> | null | undefined) {
  if (!routeGeometry) {
    return null;
  }
  const endpoints = extractRoutePreviewEndpoints(routeGeometry);
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: { role: 'route' },
        geometry: routeGeometry,
      },
      ...(endpoints
        ? [
            {
              type: 'Feature',
              properties: { role: 'route_start' },
              geometry: {
                type: 'Point',
                coordinates: endpoints.start,
              },
            },
            {
              type: 'Feature',
              properties: { role: 'route_end' },
              geometry: {
                type: 'Point',
                coordinates: endpoints.end,
              },
            },
          ]
        : []),
    ],
  } satisfies Record<string, unknown>;
}

function summarizeRoutePreviewGeometry(routeGeometry: Record<string, unknown> | null | undefined) {
  if (!routeGeometry) {
    return null;
  }
  const type = typeof routeGeometry.type === 'string' ? routeGeometry.type : 'unknown';
  const coordinates = Array.isArray(routeGeometry.coordinates) ? routeGeometry.coordinates : [];
  const segmentCount =
    type === 'MultiLineString' ? coordinates.length : type === 'LineString' ? 1 : 0;
  const pointCount =
    type === 'LineString'
      ? coordinates.length
      : type === 'MultiLineString'
        ? coordinates.reduce((total, segment) => total + (Array.isArray(segment) ? segment.length : 0), 0)
        : 0;
  return {
    type,
    segmentCount,
    pointCount,
    coordinatesPreview: coordinates.slice(0, 2),
  };
}

function summarizeRoutePreviewOverlay(overlay: Record<string, unknown> | null | undefined) {
  const features = Array.isArray(overlay?.features) ? overlay.features : [];
  return {
    featureCount: features.length,
    geometryTypes: features.map(feature =>
      feature && typeof feature === 'object' && feature !== null && typeof (feature as { geometry?: { type?: unknown } }).geometry?.type === 'string'
        ? ((feature as { geometry: { type: string } }).geometry.type as string)
        : 'unknown',
    ),
    roles: features.map(feature =>
      feature && typeof feature === 'object' && feature !== null && typeof (feature as { properties?: { role?: unknown } }).properties?.role === 'string'
        ? ((feature as { properties: { role: string } }).properties.role as string)
        : 'unknown',
    ),
  };
}

function clampRoutePreviewLatitude(latitude: number) {
  return Math.max(-85, Math.min(85, latitude));
}

function mercatorRoutePreviewY(latitude: number) {
  const radians = (clampRoutePreviewLatitude(latitude) * Math.PI) / 180;
  const sin = Math.sin(radians);
  return 0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI);
}

function computeRoutePreviewZoomFromBounds({
  bounds,
  viewportWidth,
  viewportHeight,
}: {
  bounds: [number, number, number, number];
  viewportWidth: number;
  viewportHeight: number;
}) {
  const [west, south, east, north] = bounds;
  const innerWidth = Math.max(viewportWidth - ROUTE_PREVIEW_PADDING.left - ROUTE_PREVIEW_PADDING.right, 1);
  const innerHeight = Math.max(viewportHeight - ROUTE_PREVIEW_PADDING.top - ROUTE_PREVIEW_PADDING.bottom, 1);
  const worldWest = (west + 180) / 360;
  const worldEast = (east + 180) / 360;
  const spanX = Math.max(Math.abs(worldEast - worldWest), 1e-9);
  const spanY = Math.max(Math.abs(mercatorRoutePreviewY(north) - mercatorRoutePreviewY(south)), 1e-9);
  const zoomX = Math.log2(innerWidth / (ROUTE_PREVIEW_TILE_SIZE * spanX));
  const zoomY = Math.log2(innerHeight / (ROUTE_PREVIEW_TILE_SIZE * spanY));
  const zoom = Math.min(zoomX, zoomY) - ROUTE_PREVIEW_ZOOM_SAFETY_MARGIN;
  if (!Number.isFinite(zoom)) {
    return 13;
  }
  return Math.max(ROUTE_PREVIEW_MIN_ZOOM, Math.min(ROUTE_PREVIEW_MAX_ZOOM, zoom));
}

function buildRoutePreviewViewState({
  routeGeometry,
  latitude,
  longitude,
  viewportWidth,
  viewportHeight,
}: {
  routeGeometry: Record<string, unknown> | null | undefined;
  latitude: number;
  longitude: number;
  viewportWidth?: number;
  viewportHeight?: number;
}): MapWorkbenchViewState {
  const bounds = routeGeometry ? computeMapGeometryBounds(routeGeometry) : null;
  if (bounds) {
    const [west, south, east, north] = bounds;
    return {
      center: [(west + east) / 2, (south + north) / 2],
      zoom: computeRoutePreviewZoomFromBounds({
        bounds,
        viewportWidth: viewportWidth ?? ROUTE_PREVIEW_FALLBACK_WIDTH_PX,
        viewportHeight: viewportHeight ?? ROUTE_PREVIEW_MAP_HEIGHT_PX,
      }),
      bearing: 0,
      pitch: 0,
    };
  }
  return {
    center: [longitude, latitude],
    zoom: 13,
    bearing: 0,
    pitch: 0,
  };
}

function RoutePreviewMap({
  latitude,
  longitude,
  routeGeometry,
}: {
  latitude: number;
  longitude: number;
  routeGeometry: Record<string, unknown>;
}) {
  const { basemapTheme } = usePreferences();
  const [styleJson, setStyleJson] = useState<Record<string, unknown> | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [viewportSize, setViewportSize] = useState({
    width: ROUTE_PREVIEW_FALLBACK_WIDTH_PX,
    height: ROUTE_PREVIEW_MAP_HEIGHT_PX,
  });

  const runtimeOverlayGeoJSON = useMemo(
    () => buildRoutePreviewOverlayGeoJSON(routeGeometry),
    [routeGeometry],
  );

  useEffect(() => {
    const updateViewportSize = () => {
      const nextWidth = containerRef.current?.clientWidth ?? containerRef.current?.getBoundingClientRect().width ?? 0;
      const nextHeight = containerRef.current?.clientHeight ?? containerRef.current?.getBoundingClientRect().height ?? 0;
      setViewportSize(current => {
        const width = nextWidth > 0 ? nextWidth : current.width;
        const height = nextHeight > 0 ? nextHeight : current.height;
        return width === current.width && height === current.height ? current : { width, height };
      });
    };
    updateViewportSize();
    window.addEventListener('resize', updateViewportSize);
    return () => {
      window.removeEventListener('resize', updateViewportSize);
    };
  }, []);

  const viewState = useMemo(
    () =>
      buildRoutePreviewViewState({
        routeGeometry,
        latitude,
        longitude,
        viewportWidth: viewportSize.width,
        viewportHeight: viewportSize.height,
      }),
    [latitude, longitude, routeGeometry, viewportSize.height, viewportSize.width],
  );

  useEffect(() => {
    debugSalesRequestRoutePreview('route preview widget payload', {
      latitude,
      longitude,
      routeGeometrySummary: summarizeRoutePreviewGeometry(routeGeometry),
      routeGeometry,
      runtimeOverlaySummary: summarizeRoutePreviewOverlay(runtimeOverlayGeoJSON),
      runtimeOverlayGeoJSON,
      viewState,
    });
  }, [latitude, longitude, routeGeometry, runtimeOverlayGeoJSON, viewState]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const nextStyle = await composeStyleWithSystemBasemap(
          {
            version: 8,
            sources: {},
            layers: [],
          },
          basemapTheme,
        );
        if (!cancelled) {
          debugSalesRequestRoutePreview('route preview widget style ready', {
            basemapTheme,
            layerCount: Array.isArray(nextStyle?.layers) ? nextStyle.layers.length : 0,
            sourceCount:
              nextStyle?.sources && typeof nextStyle.sources === 'object'
                ? Object.keys(nextStyle.sources as Record<string, unknown>).length
                : 0,
          });
          setStyleJson(nextStyle);
        }
      } catch (error) {
        debugSalesRequestRoutePreview('route preview widget style load failed', {
          basemapTheme,
          error: error instanceof Error ? error.message : String(error),
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [basemapTheme]);

  return (
    <div
      ref={containerRef}
      data-testid="route-preview-map-shell"
      className="overflow-hidden"
      style={{ height: `${ROUTE_PREVIEW_MAP_HEIGHT_PX}px` }}
    >
      <MapWidgetView
        className="h-full"
        styleJson={styleJson}
        status={styleJson ? 'success' : 'loading'}
        defaultViewState={viewState}
        viewState={viewState}
        inspectableLayerIds={[]}
        runtimeOverlayGeoJSON={runtimeOverlayGeoJSON}
        runtimeOverlayDebugLabel="sales-request-route-preview"
        activeToolId="select"
        drawnGeometry={null}
        scrollZoomEnabled={false}
      />
    </div>
  );
}

export default function SalesRequestWizardPage() {
  const { activeTenant } = useScope();
  const navigation = useNavigation();
  const navigationPath = (navigation as { path?: string }).path;
  const draft = useMemo(() => parseSalesRequestWizardDraft(navigationPath), [navigationPath]);

  const [activeTabId, setActiveTabId] = useState('location');
  const [entitlements, setEntitlements] = useState<ResellerEntitlement[]>([]);
  const [selectedEntitlementId, setSelectedEntitlementId] = useState('');
  const [evaluation, setEvaluation] = useState<SalesRequestEvaluation | null>(null);
  const [selectedServices, setSelectedServices] = useState<SelectionState>({});
  const [selectedOffers, setSelectedOffers] = useState<OfferSelectionState>({});
  const [loadingEntitlements, setLoadingEntitlements] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [companyName, setCompanyName] = useState('');

  const requestedCatalogOwnerTenantId = draft?.sales_context?.catalog_owner_tenant_id?.trim() || '';

  const scopedEntitlements = useMemo(() => {
    if (!requestedCatalogOwnerTenantId) return entitlements;
    return entitlements.filter(item => item.catalog_owner_tenant_id === requestedCatalogOwnerTenantId);
  }, [entitlements, requestedCatalogOwnerTenantId]);

  useEffect(() => {
    if (!activeTenant?.id) {
      setEntitlements([]);
      setSelectedEntitlementId('');
      return;
    }
    setLoadingEntitlements(true);
    setError(null);
    void listResellerEntitlements({ limit: 200 })
      .then(page => setEntitlements(page.items))
      .catch(err => {
        setError(getCommerceErrorMessage(err, 'Failed to load seller entitlements.'));
        setEntitlements([]);
      })
      .finally(() => setLoadingEntitlements(false));
  }, [activeTenant?.id]);

  useEffect(() => {
    setSelectedEntitlementId(current => {
      if (current && scopedEntitlements.some(item => item.entitlement_id === current)) return current;
      const preferred = draft?.sales_context?.entitlement_id?.trim();
      if (preferred && scopedEntitlements.some(item => item.entitlement_id === preferred)) return preferred;
      return scopedEntitlements[0]?.entitlement_id || '';
    });
  }, [draft?.sales_context?.entitlement_id, scopedEntitlements]);

  useEffect(() => {
    if (!draft || !selectedEntitlementId) {
      setEvaluation(null);
      return;
    }
    setEvaluating(true);
    setError(null);
    void evaluateSalesRequest({
      entitlement_id: selectedEntitlementId,
      catalog_owner_tenant_id: requestedCatalogOwnerTenantId || undefined,
      location: draft.location,
      route_preview: draft.route_preview,
    })
      .then(result => {
        setEvaluation(result);
        setSelectedServices({});
        setSelectedOffers({});
      })
      .catch(err => {
        setError(getCommerceErrorMessage(err, 'Failed to evaluate this sales request.'));
        setEvaluation(null);
      })
      .finally(() => setEvaluating(false));
  }, [draft, requestedCatalogOwnerTenantId, selectedEntitlementId]);

  const variantServiceOptions = useMemo(() => buildVariantServiceOptions(evaluation), [evaluation]);

  const selectedLineItems = useMemo(() => {
    return variantServiceOptions
      .map(option => {
        const key = variantSelectionKey(option);
        const quantity = selectedServices[key] ?? 0;
        const selectedOffer = resolveOfferForVariant(option, selectedOffers[key]);
        return { option, quantity, selectedOffer };
      })
      .filter(item => item.quantity > 0 && item.selectedOffer)
      .map(item => ({
        kind: item.option.kind,
        product_id: item.option.product_id,
        variant_id: item.option.variant_id,
        offer_id: item.selectedOffer?.offer_id || undefined,
        quantity: item.quantity,
      }));
  }, [selectedOffers, selectedServices, variantServiceOptions]);

  const handleSubmit = async () => {
    if (!draft || !evaluation || !selectedEntitlementId || selectedLineItems.length === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      if (evaluation.recommended_action === 'cart') {
        await createSalesRequestCart({
          entitlement_id: selectedEntitlementId,
          catalog_owner_tenant_id: requestedCatalogOwnerTenantId || undefined,
          location: draft.location,
          route_preview: draft.route_preview,
          interested_line_items: selectedLineItems,
        });
        clearSalesRequestWizardDraft(navigationPath);
        navigation.navigate('/reseller/carts');
        return;
      }
      const quote = await createSalesRequestQuote({
        entitlement_id: selectedEntitlementId,
        catalog_owner_tenant_id: requestedCatalogOwnerTenantId || undefined,
        location: draft.location,
        route_preview: draft.route_preview,
        customer_snapshot: { first_name: firstName, last_name: lastName, email, phone, company_name: companyName },
        interested_line_items: selectedLineItems,
      });
      clearSalesRequestWizardDraft(navigationPath);
      navigation.navigate(`/agent/quotes?quote_id=${encodeURIComponent(quote.friendly_quote_id || quote.quote_id)}`);
    } catch (err) {
      setError(getCommerceErrorMessage(err, 'Failed to create the sales request.'));
    } finally {
      setSubmitting(false);
    }
  };

  const selectedEntitlement = scopedEntitlements.find(e => e.entitlement_id === selectedEntitlementId);
  const actionLabel = evaluation?.recommended_action === 'cart' ? 'Create Cart' : 'Create Quote';
  const canSubmit =
    !submitting &&
    !evaluating &&
    selectedLineItems.length > 0 &&
    !!evaluation &&
    evaluation.recommended_action !== 'blocked';
  const routeEstimate: AdminEligibilityPreviewPreliminaryEstimate | null =
    draft?.route_preview?.preliminary_estimate ?? null;

  const summaryItems = useMemo(
    () => [
      {
        label: 'Location',
        value: draft?.location.display_name || draft?.location.address_line1 || 'No draft',
        complete: !!draft,
      },
      {
        label: 'Entitlement',
        value: selectedEntitlement?.name || (loadingEntitlements ? 'Loading…' : 'Not selected'),
        complete: !!selectedEntitlement,
      },
      {
        label: 'Services',
        value: selectedLineItems.length > 0 ? `${selectedLineItems.length} selected` : 'None selected',
        complete: selectedLineItems.length > 0,
      },
      {
        label: 'Customer',
        value: email.trim() ? email : 'Optional for carts',
        complete: evaluation?.recommended_action === 'cart' || Boolean(email.trim()),
      },
    ],
    [draft, selectedEntitlement, loadingEntitlements, selectedLineItems.length, email, evaluation?.recommended_action],
  );

  const eligibilityCallout =
    evaluation?.recommended_action === 'blocked'
      ? 'This location is blocked for seller-side fulfillment.'
      : evaluation?.recommended_action === 'cart'
        ? 'Direct checkout available — no quote required.'
        : evaluation?.recommended_action === 'quote'
          ? 'This location requires a seller-side quote.'
          : evaluating
            ? 'Evaluating eligibility…'
            : null;

  return (
    <WizardWorkspace
      isOpen={true}
      title="New Sales Request"
      subtitle="Evaluate this sales-map location in seller context and create a quote or cart."
      tabs={[
        { id: 'location', label: 'Location', icon: <MapPin className="h-4 w-4" /> },
        { id: 'seller', label: 'Seller Context', icon: <Settings2 className="h-4 w-4" /> },
        {
          id: 'services',
          label: 'Services',
          icon: <ShoppingCart className="h-4 w-4" />,
          count: selectedLineItems.length > 0 ? selectedLineItems.length : undefined,
        },
        { id: 'customer', label: 'Customer', icon: <User className="h-4 w-4" /> },
        { id: 'review', label: 'Review', icon: <CheckCircle2 className="h-4 w-4" /> },
      ]}
      activeTabId={activeTabId}
      onTabChange={setActiveTabId}
      onSave={() => void handleSubmit()}
      onClose={() => navigation.navigate('/sales')}
      isSaving={submitting}
      saveLabel={actionLabel}
      discardLabel="Back to Sales Map"
      summaryItems={summaryItems}
      summaryCallout={eligibilityCallout}
    >
      {/* LOCATION */}
      {activeTabId === 'location' ? (
        <VerticalStack gap={6}>
          <PaneHeader
            title="Location"
            subtitle="The sales-map location carried into this seller workflow."
            sticky={false}
          />
          <div className="px-6 pb-8">
            {!draft ? (
              <PaneSection label="No draft found">
                <Callout variant="warning">
                  No sales-map request draft found. Launch this wizard from the Sales Map so the selected location and
                  route preview context can be carried into the seller workflow.
                </Callout>
              </PaneSection>
            ) : (
              <VerticalStack gap={8}>
                <PaneSection label="Service address">
                  <Surface className="border-border/60 bg-muted/10 p-5">
                    <VerticalStack gap={2}>
                      <p className="text-lg font-bold text-foreground">
                        {draft.location.display_name || draft.location.address_line1 || draft.location.feature_identity_key}
                      </p>
                      <DescriptionText className="text-xs">
                        Occupancy: {draft.location.occupancy_kind || 'unknown'}
                      </DescriptionText>
                    </VerticalStack>
                  </Surface>
                </PaneSection>

                {draft.route_preview ? (
                  <PaneSection label="Route preview">
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
	                      <Surface className="border-border/60 bg-muted/10 p-4">
	                        <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/80">
	                          Total build distance
	                        </p>
	                        <p className="mt-1 text-base font-bold text-foreground">
	                          {Math.round(draft.route_preview.total_build_distance_m).toLocaleString()} m
	                        </p>
	                        <p className="mt-1 text-xs text-muted-foreground">
	                          Origin {Math.round(draft.route_preview.origin_approach_distance_m).toLocaleString()} m · Roadway{' '}
	                          {Math.round(draft.route_preview.road_distance_m).toLocaleString()} m · Access{' '}
	                          {Math.round(draft.route_preview.destination_approach_distance_m).toLocaleString()} m
	                        </p>
	                      </Surface>
                      {draft.route_preview.distance_quality ? (
                        <Surface className="border-border/60 bg-muted/10 p-4">
                          <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/80">
                            Distance quality
                          </p>
                          <p className="mt-1 text-base font-bold text-foreground">
                            {draft.route_preview.distance_quality}
                          </p>
                        </Surface>
                      ) : null}
                      {draft.route_preview.routing_profile ? (
                        <Surface className="border-border/60 bg-muted/10 p-4">
                          <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/80">
                            Routing profile
                          </p>
                          <p className="mt-1 text-base font-bold text-foreground">
                            {formatRoutingProfile(draft.route_preview.routing_profile)}
                          </p>
                        </Surface>
                      ) : null}
                      {routeEstimate?.estimated_quote_amount != null ? (
                        <Surface className="border-border/60 bg-muted/10 p-4">
                          <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/80">
                            Estimated quote
                          </p>
                          <p className="mt-1 text-base font-bold text-foreground">
                            {formatEstimateCurrency(routeEstimate.estimated_quote_amount, routeEstimate.currency)}
                          </p>
                        </Surface>
                      ) : null}
                    </div>
                  </PaneSection>
                ) : null}

                {routeEstimate ? (
                  <PaneSection label="Preliminary estimate">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                      <Surface className="border-border/60 bg-muted/10 p-4">
                        <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/80">
                          Estimate status
                        </p>
                        <p className="mt-1 text-base font-bold text-foreground">
                          {routeEstimate.estimate_status || 'Not available'}
                        </p>
                      </Surface>
                      <Surface className="border-border/60 bg-muted/10 p-4">
                        <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/80">
                          Estimated quote
                        </p>
                        <p className="mt-1 text-base font-bold text-foreground">
                          {formatEstimateCurrency(routeEstimate.estimated_quote_amount, routeEstimate.currency)}
                        </p>
                      </Surface>
	                      <Surface className="border-border/60 bg-muted/10 p-4">
	                        <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/80">
	                          Price / m
	                        </p>
	                        <p className="mt-1 text-base font-bold text-foreground">
	                          {formatEstimateCurrency(routeEstimate.price_per_meter, routeEstimate.currency)}
	                        </p>
	                      </Surface>
	                      <Surface className="border-border/60 bg-muted/10 p-4">
	                        <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/80">
	                          Build type
	                        </p>
	                        <p className="mt-1 text-base font-bold text-foreground">
	                          {routeEstimate.build_type || 'Not selected'}
	                        </p>
	                      </Surface>
                    </div>
                    {routeEstimate.price_source || routeEstimate.reason_code || routeEstimate.disclaimer ? (
                      <DescriptionText className="mt-3 text-xs">
                        {[
                          routeEstimate.price_source ? `Price source: ${routeEstimate.price_source}` : null,
                          routeEstimate.reason_code ? `Reason: ${routeEstimate.reason_code}` : null,
                          routeEstimate.disclaimer || null,
                        ]
                          .filter(Boolean)
                          .join(' • ')}
                      </DescriptionText>
                    ) : null}
                  </PaneSection>
                ) : null}

                {draft.route_preview?.route_geometry ? (
                  <PaneSection label="Route map">
                    <RoutePreviewMap
                      latitude={draft.location.latitude}
                      longitude={draft.location.longitude}
                      routeGeometry={draft.route_preview.route_geometry}
                    />
                  </PaneSection>
                ) : null}
              </VerticalStack>
            )}
          </div>
        </VerticalStack>
      ) : null}

      {/* SELLER CONTEXT */}
      {activeTabId === 'seller' ? (
        <VerticalStack gap={6}>
          <PaneHeader
            title="Seller Context"
            subtitle="Choose the entitled seller publication that drives pricing and eligibility."
          />
          <div className="px-6 pb-8">
            <PaneSection label="Entitlement">
              <VerticalStack gap={4}>
                {requestedCatalogOwnerTenantId ? (
                  <Callout variant="info">
                    Scoped to seller tenant:{' '}
                    <span className="font-bold">{requestedCatalogOwnerTenantId}</span>
                  </Callout>
                ) : null}
                {!loadingEntitlements && scopedEntitlements.length === 0 ? (
                  <Callout variant="warning">
                    No seller entitlements are available for this seller context. Creating a request requires an approved
                    reseller entitlement for the selected seller tenant.
                  </Callout>
                ) : null}
                <FormField label="Reseller entitlement">
                  <select
                    value={selectedEntitlementId}
                    onChange={event => setSelectedEntitlementId(event.target.value)}
                    className={selectCls}
                    disabled={loadingEntitlements || submitting}
                  >
                    {loadingEntitlements ? <option value="">Loading entitlements…</option> : null}
                    {!loadingEntitlements && scopedEntitlements.length === 0 ? (
                      <option value="">No entitlements available</option>
                    ) : null}
                    {scopedEntitlements.map(item => (
                      <option key={item.entitlement_id} value={item.entitlement_id}>
                        {item.name} ({item.catalog_owner_tenant_id})
                      </option>
                    ))}
                  </select>
                </FormField>
              </VerticalStack>
            </PaneSection>
          </div>
        </VerticalStack>
      ) : null}

      {/* SERVICES */}
      {activeTabId === 'services' ? (
        <VerticalStack gap={6}>
          <PaneHeader
            title="Services"
            subtitle="Select one or more eligible services before creating the seller-side request."
            sticky={false}
            badges={
              evaluation ? (
                <RegistryStatus
                  label={
                    evaluation.recommended_action === 'cart'
                      ? 'Direct Checkout'
                      : evaluation.recommended_action === 'quote'
                        ? 'Quote Required'
                        : 'Blocked'
                  }
                  variant={
                    evaluation.recommended_action === 'blocked'
                      ? 'error'
                      : evaluation.recommended_action === 'cart'
                        ? 'success'
                        : 'warning'
                  }
                />
              ) : undefined
            }
          />
          <div className="px-6 pb-8">
            <VerticalStack gap={6}>
              {error ? <Callout variant="error">{error}</Callout> : null}
              {evaluation?.evaluation.customer_message ? (
                <Callout variant="info">{evaluation.evaluation.customer_message}</Callout>
              ) : null}
              {evaluation?.recommended_action === 'blocked' ? (
                <Callout variant="error">
                  No sellable seller-side outcome is available for this address and entitlement.
                </Callout>
              ) : null}

              <PaneSection label="Sellable services">
                {evaluating ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="h-24 animate-pulse rounded-lg border border-border/40 bg-muted/10" />
                    ))}
                  </div>
                ) : variantServiceOptions.length > 0 ? (
                  <div className="space-y-5">
                    {Array.from(
                      variantServiceOptions.reduce((map, option) => {
                        const group = map.get(option.product_id);
                        if (group) group.push(option);
                        else map.set(option.product_id, [option]);
                        return map;
                      }, new Map<string, SalesRequestVariantOption[]>()),
                    ).map(([productId, variants]) => (
                      <div key={productId}>
                        <div className="mb-3 flex items-center gap-3">
                          <p className="text-sm font-bold text-foreground">{variants[0].product_name}</p>
                          <div className="h-px flex-1 bg-border/40" />
                        </div>
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                          {variants.map(option => {
                            const key = variantSelectionKey(option);
                            const quantity = selectedServices[key] ?? 0;
                            const isSelected = quantity > 0;
                            const selectedOffer = resolveOfferForVariant(option, selectedOffers[key] || option.offer_id);
                            const multiOffer = option.offers.length > 1;
                            return (
                              <div
                                key={key}
                                onClick={() =>
                                  !submitting &&
                                  setSelectedServices(current => ({
                                    ...current,
                                    [key]: isSelected ? 0 : 1,
                                  }))
                                }
                                className={`cursor-pointer rounded-lg border-2 px-4 py-3 transition-all ${
                                  isSelected
                                    ? 'border-primary/30 border-l-primary bg-primary/5'
                                    : 'border-border/30 bg-muted/5 hover:border-border/60 hover:bg-muted/10'
                                }`}
                              >
                                <div className="flex items-start gap-3">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={e =>
                                      setSelectedServices(current => ({
                                        ...current,
                                        [key]: e.target.checked ? 1 : 0,
                                      }))
                                    }
                                    onClick={e => e.stopPropagation()}
                                    disabled={submitting}
                                    className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
                                  />
                                  <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                      <p className="text-sm font-bold text-foreground">{option.display_name}</p>
                                      <RegistryStatus label={option.kind} variant="muted" />
                                    </div>
                                    {option.description ? (
                                      <DescriptionText className="mt-0.5 text-xs">{option.description}</DescriptionText>
                                    ) : null}
                                  </div>
                                  <div className="shrink-0 text-right" onClick={e => e.stopPropagation()}>
                                    <p className="text-sm font-bold text-foreground">
                                      {selectedOffer?.currency
                                        ? formatCurrency(
                                            selectedOffer.currency,
                                            (selectedOffer.discounted_price ?? selectedOffer.price ?? 0) / 100,
                                          )
                                        : option.display_name}
                                    </p>
                                    {selectedOffer?.billing_period_months ? (
                                      <DescriptionText className="text-[11px]">{offerBillingLabel(selectedOffer.billing_period_months)}</DescriptionText>
                                    ) : null}
                                    {multiOffer ? (
                                      <select
                                        aria-label={`Offer for ${option.display_name}`}
                                        value={selectedOffer?.offer_id || ''}
                                        onChange={e =>
                                          setSelectedOffers(current => ({
                                            ...current,
                                            [key]: e.target.value,
                                          }))
                                        }
                                        className="mt-1.5 h-7 max-w-[140px] rounded border border-border/60 bg-background px-2 text-xs font-medium text-foreground outline-none focus:border-primary/40 disabled:opacity-50"
                                        disabled={submitting}
                                      >
                                        {option.offers.map(offer => (
                                          <option key={offer.offer_id} value={offer.offer_id}>
                                            {offer.display_name} •{' '}
                                            {offer.currency
                                              ? formatCurrency(
                                                  offer.currency,
                                                  (offer.discounted_price ?? offer.price ?? 0) / 100,
                                                )
                                              : offer.display_name}
                                          </option>
                                        ))}
                                      </select>
                                    ) : null}
                                    {isSelected ? (
                                      <div className="mt-1.5 flex items-center justify-end gap-1.5">
                                        <button
                                          type="button"
                                          onClick={e => {
                                            e.stopPropagation();
                                            setSelectedServices(current => ({
                                              ...current,
                                              [key]: Math.max(1, (current[key] ?? 1) - 1),
                                            }));
                                          }}
                                          disabled={submitting || quantity <= 1}
                                          className="flex h-6 w-6 items-center justify-center rounded border border-border/60 bg-background text-xs font-bold transition-all hover:bg-muted disabled:opacity-40"
                                        >
                                          −
                                        </button>
                                        <span className="w-5 text-center text-xs font-bold text-foreground">{quantity}</span>
                                        <button
                                          type="button"
                                          onClick={e => {
                                            e.stopPropagation();
                                            setSelectedServices(current => ({
                                              ...current,
                                              [key]: Math.min(option.limit_per_cart || 99, (current[key] ?? 1) + 1),
                                            }));
                                          }}
                                          disabled={submitting || quantity >= (option.limit_per_cart || 99)}
                                          className="flex h-6 w-6 items-center justify-center rounded border border-border/60 bg-background text-xs font-bold transition-all hover:bg-muted disabled:opacity-40"
                                        >
                                          +
                                        </button>
                                      </div>
                                    ) : null}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <DescriptionText className="text-xs">
                    {!evaluation
                      ? 'Complete the Seller Context step to load eligible services.'
                      : 'No sellable services are available for the selected entitlement and address.'}
                  </DescriptionText>
                )}
              </PaneSection>

              {evaluation?.evaluation.blocked_products?.length ? (
                <PaneSection label="Blocked services">
                  <div className="space-y-2">
                    {evaluation.evaluation.blocked_products.map(product => (
                      <div key={product.product_id} className="rounded-lg border border-destructive/20 bg-destructive/5 p-3">
                        <p className="text-sm font-bold text-foreground">{product.display_name}</p>
                        <DescriptionText className="mt-1 text-xs">
                          {product.reasons.join(', ')}
                          {product.customer_hint ? ` • ${product.customer_hint}` : ''}
                        </DescriptionText>
                      </div>
                    ))}
                  </div>
                </PaneSection>
              ) : null}
            </VerticalStack>
          </div>
        </VerticalStack>
      ) : null}

      {/* CUSTOMER */}
      {activeTabId === 'customer' ? (
        <VerticalStack gap={6}>
          <PaneHeader
            title="Customer"
            subtitle="Customer information stored on seller-side quotes. Cart creation can proceed without it."
          />
          <div className="px-6 pb-8">
            <PaneSection label="Customer snapshot">
              <VerticalStack gap={4}>
                {evaluation?.recommended_action === 'cart' ? (
                  <Callout variant="info">
                    Direct checkout carts do not require customer information. You may leave this section blank.
                  </Callout>
                ) : null}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <FormField label="First name">
                    <input
                      aria-label="First name"
                      value={firstName}
                      onChange={event => setFirstName(event.target.value)}
                      placeholder="Jane"
                      className={inputCls}
                      disabled={submitting}
                    />
                  </FormField>
                  <FormField label="Last name">
                    <input
                      aria-label="Last name"
                      value={lastName}
                      onChange={event => setLastName(event.target.value)}
                      placeholder="Smith"
                      className={inputCls}
                      disabled={submitting}
                    />
                  </FormField>
                </div>
                <FormField label="Email address">
                  <input
                    aria-label="Email address"
                    type="email"
                    value={email}
                    onChange={event => setEmail(event.target.value)}
                    placeholder="jane.smith@example.com"
                    className={inputCls}
                    disabled={submitting}
                  />
                </FormField>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <FormField label="Phone number">
                    <input
                      aria-label="Phone number"
                      type="tel"
                      value={phone}
                      onChange={event => setPhone(event.target.value)}
                      placeholder="+1 (555) 000-0000"
                      className={inputCls}
                      disabled={submitting}
                    />
                  </FormField>
                  <FormField label="Company name">
                    <input
                      aria-label="Company name"
                      value={companyName}
                      onChange={event => setCompanyName(event.target.value)}
                      placeholder="Acme Corp"
                      className={inputCls}
                      disabled={submitting}
                    />
                  </FormField>
                </div>
              </VerticalStack>
            </PaneSection>
          </div>
        </VerticalStack>
      ) : null}

      {/* REVIEW */}
      {activeTabId === 'review' ? (
        <VerticalStack gap={6}>
          <PaneHeader
            title="Review"
            subtitle="Verify the request before creating the seller-side quote or cart."
          />
          <div className="px-6 pb-8">
            <VerticalStack gap={6}>
              {error ? <Callout variant="error">{error}</Callout> : null}

              <PaneSection label="Request summary">
                <Surface className="border-border/60 bg-muted/10 p-5">
                  <VerticalStack gap={4}>
                    <HorizontalStack className="items-start justify-between gap-3 text-sm">
                      <span className="shrink-0 text-muted-foreground">Location</span>
                      <span className="text-right font-bold text-foreground">
                        {draft?.location.display_name || draft?.location.address_line1 || 'No draft'}
                      </span>
                    </HorizontalStack>
                    <HorizontalStack className="items-start justify-between gap-3 text-sm">
                      <span className="shrink-0 text-muted-foreground">Entitlement</span>
                      <span className="text-right font-bold text-foreground">
                        {selectedEntitlement?.name || 'Not selected'}
                      </span>
                    </HorizontalStack>
                    <HorizontalStack className="items-start justify-between gap-3 text-sm">
                      <span className="shrink-0 text-muted-foreground">Outcome type</span>
                      <span className="text-right font-bold capitalize text-foreground">
                        {evaluation?.recommended_action ?? 'Unknown'}
                      </span>
                    </HorizontalStack>
                    {evaluation && selectedLineItems.length > 0 ? (
                      <div>
                        <p className="mb-2 text-sm text-muted-foreground">Selected services</p>
                        <div className="space-y-2">
                          {variantServiceOptions
                            .filter(o => (selectedServices[variantSelectionKey(o)] ?? 0) > 0)
                            .map(o => {
                              const key = variantSelectionKey(o);
                              const selectedOffer = resolveOfferForVariant(o, selectedOffers[key] || o.offer_id);
                              return (
                                <div key={key} className="flex items-center justify-between text-sm">
                                  <span className="font-medium text-foreground">
                                    {o.display_name}
                                    {selectedOffer?.display_name ? ` • ${selectedOffer.display_name}` : ''}
                                  </span>
                                  <span className="font-bold text-muted-foreground">
                                    × {selectedServices[key]}
                                  </span>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    ) : null}
                    {evaluation?.recommended_action === 'quote' && (firstName || lastName || email) ? (
                      <HorizontalStack className="items-start justify-between gap-3 text-sm">
                        <span className="shrink-0 text-muted-foreground">Customer</span>
                        <span className="text-right font-bold text-foreground">
                          {[firstName, lastName].filter(Boolean).join(' ') || email}
                        </span>
                      </HorizontalStack>
                    ) : null}
                  </VerticalStack>
                </Surface>
              </PaneSection>

              {!canSubmit ? (
                <Callout variant="warning">
                  {selectedLineItems.length === 0
                    ? 'Select at least one service on the Services step before creating the request.'
                    : evaluation?.recommended_action === 'blocked'
                      ? 'This location is blocked — no seller-side request can be created.'
                      : 'Complete the previous steps before submitting.'}
                </Callout>
              ) : null}

              <PaneFooter>
                <DescriptionText>
                  {evaluation?.recommended_action === 'cart'
                    ? 'A seller-side direct checkout cart will be created and opened in your cart workspace.'
                    : 'A seller-side quote will be created and opened in your quotes workspace.'}
                </DescriptionText>
              </PaneFooter>
            </VerticalStack>
          </div>
        </VerticalStack>
      ) : null}
    </WizardWorkspace>
  );
}
